import { useState, useMemo } from 'react'
import { Bell, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useTaskManager } from '@/contexts/TaskManagerContext'
import { TaskPanel } from './TaskPanel'

/**
 * Task bar component for the header.
 * Shows task count badge and opens a dropdown panel with task details.
 *
 * Replaces the simple bell icon with a comprehensive task indicator.
 */
export function TaskBar() {
  const [isOpen, setIsOpen] = useState(false)
  const { activeTasks, recentCompleted, unseenCount, markAllSeen, dismissTask, clearRecentCompleted } = useTaskManager()

  // Get processing tasks (pending + processing)
  const processingTasks = useMemo(
    () =>
      Array.from(activeTasks.values()).filter(
        (t) => t.status === 'processing' || t.status === 'pending'
      ),
    [activeTasks]
  )

  const hasProcessing = processingTasks.length > 0
  const totalBadgeCount = hasProcessing ? processingTasks.length : unseenCount

  // Mark as seen when panel opens
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open && unseenCount > 0) {
      markAllSeen()
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          {hasProcessing ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : (
            <Bell className="h-5 w-5 text-muted-foreground" />
          )}
          {totalBadgeCount > 0 && (
            <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[hsl(var(--info))] px-1 text-[10px] font-semibold text-[hsl(var(--info-foreground))]">
              {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[480px] p-4">
        <TaskPanel
          processing={processingTasks}
          completed={recentCompleted.slice(0, 5)}
          onClose={() => setIsOpen(false)}
          onDismissTask={dismissTask}
          onClearAll={clearRecentCompleted}
        />
      </PopoverContent>
    </Popover>
  )
}
